<template>
  <div class="audio-player-page" :style="{ backgroundImage: `url(${IMAGE_BASE_URL + audioData.poster})` }">
    <!-- 顶部导航栏 -->
    <div class="player-header">
      <div class="back-button" @click="goBack">
        <i class="icon-back"></i>
      </div>
      <div class="audio-title">{{ currentTrack?.title || audioData.zhName }}</div>
      <div class="more-button" @click="togglePlaylist">
        <i class="icon-playlist"></i>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="upper-container">
      <!-- 唱片区域 -->
      <div class="record-container">
        <div class="record-bg">
          <div
            class="record-img rotate-play"
            :class="{ 'playing': isPlaying }"
            :style="{ backgroundImage: `url(${IMAGE_BASE_URL + audioData.poster})` }"
          >
          </div>
        </div>
      </div>
    </div>

    <!-- 音乐播放器控制区域 -->
    <div class="audio-box">
      <div class="audio-container">
        <!-- 进度条 -->
        <div class="a-progress">
          <div
            class="pgs-total"
            ref="progressTotal"
            @mousedown="startProgressDrag"
            @click="seekTo"
          >
            <div class="pgs-play" :style="{ width: progressPercentage + '%' }"></div>
          </div>
        </div>

        <!-- 控制按钮区域 -->
        <div class="a-controls">
          <!-- 播放时长 -->
          <div class="time-container">
            <span class="played-time">{{ currentTime }}</span>&nbsp;/&nbsp;
            <span class="audio-time">{{ totalTime }}</span>
          </div>

          <!-- 中间按钮 -->
          <div class="center-button-container">
            <!-- 播放模式 -->
            <div class="center-icon mode" :class="'mode-' + playMode" @click="togglePlayMode"></div>
            <!-- 上一首 -->
            <div class="center-icon s-right" @click="playPrevious"></div>
            <!-- 播放/暂停按钮 -->
            <div
              class="play-pause-btn"
              :class="isPlaying ? 'icon-pause' : 'icon-play'"
              @click="togglePlay"
            ></div>
            <!-- 下一首 -->
            <div class="center-icon s-left" @click="playNext"></div>
            <!-- 音量调节 -->
            <div
              class="center-icon volume"
              :class="{ 'muted': volume === 0 }"
              @click="toggleMute"
            ></div>
            <!-- 音量滑块 -->
            <input
              type="range"
              class="volume-slider"
              v-model="volumeSlider"
              min="0"
              max="100"
              step="1"
              @input="updateVolume"
            >
          </div>

          <!-- 右侧按钮 -->
          <div class="bottom-button-container">
            <!-- 列表 -->
            <div class="bottom-icon list" @click="togglePlaylist"></div>
            <!-- 倍速 -->
            <div class="speed" @click="toggleSpeed">{{ playbackRate }}X</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 播放列表 -->
    <div v-show="showPlaylist" class="close-list" @click="hidePlaylist"></div>
    <div class="music-list" :class="playlistAnimationClass">
      <div class="music-list-container">
        <div class="music-list-title">تىزىملىك</div>
        <hr class="line">
        <div class="all-list">
          <div
            v-for="(episode, index) in audioData.episodes"
            :key="episode.id"
            :class="{ 'active': currentEpisodeIndex === index }"
            @click="playEpisode(index, episode.id)"
          >
            {{ episode.title }}
          </div>
        </div>
      </div>
    </div>

    <!-- 注意：不再使用本地 audio 元素，使用全局音频管理器 -->
  </div>
</template>

<script>
import { getDetail, getEpisodeList } from '../api';
import { IMAGE_BASE_URL } from '../utils/constants';
import audioManager from '../utils/audioManager';

export default {
  name: 'AudioPlayer',
  components: {},
  props: {
    audioId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    // 从音频管理器获取初始状态
    const audioState = audioManager.getState();
    return {
      ...audioState,
      IMAGE_BASE_URL,
      isLoading: false,
      showPlaylist: false,
      // 添加取消订阅函数的引用
      unsubscribeAudioManager: null
    };
  },
  computed: {
    currentTrack() {
      return this.audioData.episodes?.[this.currentEpisodeIndex];
    },

    playlistAnimationClass() {
      if (this.showPlaylist) {
        return 'list-card-show';
      } else {
        return 'list-card-hide';
      }
    }
  },
  watch: {
    'audioData.poster'(newPoster) {
      if (newPoster) {
        console.log('Poster updated:', newPoster);
        console.log('Full image URL:', this.IMAGE_BASE_URL + newPoster);

        // 预加载图片以确保能正常显示
        const img = new Image();
        img.onload = () => {
          console.log('Background image preloaded successfully');
        };
        img.onerror = () => {
          console.error('Failed to preload background image');
        };
        img.src = this.IMAGE_BASE_URL + newPoster;
      }
    }
  },
  mounted() {
    // 订阅音频管理器状态变化
    this.unsubscribeAudioManager = audioManager.subscribe(this.handleAudioStateChange);

    // 监听音频管理器的播放剧集事件
    window.addEventListener('audioManager:playEpisode', this.handlePlayEpisodeEvent);

    // 如果有传入的音频ID，加载对应数据
    if (this.audioId) {
      this.fetchAudioData(this.audioId);
    }

    // 监听全局音频播放状态变化
    window.addEventListener('beforeunload', this.handlePageUnload);

    // 监听来自浮动播放器的同步事件
    window.addEventListener('syncFromFloatingPlayer', this.handleSyncFromFloatingPlayer);

    // 监听音频会话恢复事件
    window.addEventListener('audioSessionRestored', this.handleSessionRestored);
  },
  beforeUnmount() {
    // 取消订阅音频管理器
    if (this.unsubscribeAudioManager) {
      this.unsubscribeAudioManager();
    }

    // 清理事件监听
    window.removeEventListener('beforeunload', this.handlePageUnload);
    window.removeEventListener('audioManager:playEpisode', this.handlePlayEpisodeEvent);
    window.removeEventListener('syncFromFloatingPlayer', this.handleSyncFromFloatingPlayer);
    window.removeEventListener('audioSessionRestored', this.handleSessionRestored);
    document.removeEventListener('mousemove', this.onProgressDrag);
    document.removeEventListener('mouseup', this.endProgressDrag);

    // 通知浮动播放器当前状态
    this.notifyFloatingPlayer();
  },
  methods: {
    // 处理音频管理器状态变化
    handleAudioStateChange(newState) {
      // 批量更新状态，避免频繁的响应式更新
      const updates = {};
      Object.keys(newState).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(this.$data, key)) {
          updates[key] = newState[key];
        }
      });
      
      // 使用Object.assign一次性更新所有状态
      Object.assign(this.$data, updates);
      
      // 在下一个tick后强制更新UI
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // 处理播放剧集事件
    handlePlayEpisodeEvent(event) {
      const { index, episodeId } = event.detail;
      // 来自audioManager的事件强制播放（上一首/下一首按钮）
      this.playEpisode(index, episodeId, true);
    },

    // 处理来自浮动播放器的同步事件
    handleSyncFromFloatingPlayer(event) {
      const { type, data } = event.detail;

      switch (type) {
        case 'play':
        case 'pause':
          // 播放状态已通过音频管理器同步，无需额外处理
          break;
        case 'seek':
          audioManager.seekTo(data.percentage);
          break;
        case 'volume':
          audioManager.setVolume(data.volume);
          break;
        case 'episode':
          this.playEpisode(data.index, data.episodeId, false);
          break;
      }
    },

    // 处理音频会话恢复事件
    handleSessionRestored(event) {
      const { audioInfo, savedState } = event.detail;
      console.log('AudioPlayer 收到会话恢复事件:', audioInfo, savedState);

      // 如果当前没有加载音频，且恢复的音频ID与当前不同，可以选择加载完整数据
      if (!this.audioData.id || this.audioData.id !== audioInfo.id) {
        // 可以选择重新获取完整的音频数据
        this.fetchAudioDataFromSaved(audioInfo, savedState);
      }
    },

    // 从保存的信息中恢复完整音频数据
    async fetchAudioDataFromSaved(audioInfo, savedState) {
      try {
        console.log('从保存信息恢复完整音频数据:', audioInfo.id);

        // 重新获取完整的音频数据
        const response = await getDetail(audioInfo.id);
        const fullAudioData = response.data;

        // 更新音频管理器的完整数据
        audioManager.setAudioData(fullAudioData);

        // 如果有剧集数据，恢复当前剧集
        if (fullAudioData.episodes && fullAudioData.episodes.length > savedState.currentEpisodeIndex) {
          const currentEpisode = fullAudioData.episodes[savedState.currentEpisodeIndex];

          // 获取当前剧集的播放URL
          const episodeList = await getEpisodeList(currentEpisode.id, 720);
          if (episodeList && episodeList.data && episodeList.data.playConfig) {
            fullAudioData.audioUrl = episodeList.data.playConfig.url;
            audioManager.setAudioSource(fullAudioData.audioUrl);
          }
        }

        console.log('成功恢复完整音频数据');
      } catch (error) {
        console.error('恢复完整音频数据失败:', error);
      }
    },

    goBack() {
      // 通知浮动播放器当前播放状态
      this.notifyFloatingPlayer();

      // 通知父组件返回
      this.$emit('close-audio');
      this.$emit('update:audioId', null);
    },
    
    notifyFloatingPlayer() {
      console.log('AudioPlayer notifyFloatingPlayer called');
      console.log('audioData.audioUrl:', this.audioData.audioUrl);
      console.log('currentTime:', this.currentTime);
      
      if (this.audioData.audioUrl) {
        const eventData = {
          audioData: this.audioData,
          isPlaying: this.isPlaying,
          currentTime: this.$refs.audioPlayer?.currentTime || 0,
          currentEpisodeIndex: this.currentEpisodeIndex
        };
        
        console.log('Dispatching showFloatingPlayer event with data:', eventData);
        
        // 通过自定义事件通知浮动播放器
        const event = new CustomEvent('showFloatingPlayer', {
          detail: eventData
        });
        window.dispatchEvent(event);
      }
    },


    // 这些方法不再需要，因为音频事件由音频管理器处理

    togglePlay() {
      audioManager.togglePlay();
    },

    playPrevious() {
      audioManager.playPrevious();
    },

    playNext() {
      audioManager.playNext();
    },

    playRandom() {
      audioManager.playRandom();
    },

    togglePlayMode() {
      audioManager.togglePlayMode();
    },

    toggleMute() {
      audioManager.toggleMute();
    },

    updateVolume() {
      audioManager.setVolume(this.volumeSlider / 100);
    },

    toggleSpeed() {
      const speeds = [0.5, 1.0, 1.5, 2.0];
      const currentIndex = speeds.indexOf(this.playbackRate);
      const nextIndex = (currentIndex + 1) % speeds.length;
      audioManager.setPlaybackRate(speeds[nextIndex]);
    },

    seekTo(event) {
      const progressTotal = this.$refs.progressTotal;
      if (!progressTotal) return;

      const rect = progressTotal.getBoundingClientRect();
      const clickX = event.offsetX;
      const percentage = (clickX / rect.width) * 100;

      audioManager.seekTo(percentage);
    },

    startProgressDrag(event) {
      event.preventDefault();
      audioManager.setDragging(true);

      // 添加全局事件监听
      document.addEventListener('mousemove', this.onProgressDrag);
      document.addEventListener('mouseup', this.endProgressDrag);

      // 立即处理当前位置
      this.onProgressDrag(event);
    },

    onProgressDrag(event) {
      if (!this.isDragging) return;

      const progressTotal = this.$refs.progressTotal;
      if (!progressTotal) return;

      const rect = progressTotal.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = (clickX / rect.width) * 100;

      // 更新进度条显示（临时显示，不影响实际播放位置）
      this.progressPercentage = percentage;

      // 更新时间显示
      const audioState = audioManager.getState();
      if (audioState.totalTime !== '00:00') {
        const totalSeconds = this.parseTime(audioState.totalTime);
        const newTime = totalSeconds * (percentage / 100);
        this.currentTime = this.formatTime(newTime);
      }
    },

    endProgressDrag(event) {
      if (!this.isDragging) return;

      audioManager.setDragging(false);

      // 移除全局事件监听
      document.removeEventListener('mousemove', this.onProgressDrag);
      document.removeEventListener('mouseup', this.endProgressDrag);

      // 设置音频播放位置
      const progressTotal = this.$refs.progressTotal;
      if (!progressTotal) return;

      const rect = progressTotal.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = (clickX / rect.width) * 100;

      audioManager.seekTo(percentage);
    },



    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    parseTime(timeString) {
      const parts = timeString.split(':');
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    },

    async playEpisode(index, episodeId, forcePlay = false) {
      if (episodeId === undefined) {
        console.error('音频ID未定义，无法播放');
        return;
      }

      if (index === this.currentEpisodeIndex && this.audioData.audioUrl) {
        // 如果点击的是当前剧集
        if (forcePlay) {
          // 强制播放：重新从头开始播放
          audioManager.audio.currentTime = 0;
          try {
            await audioManager.audio.play();
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('强制播放失败:', error);
            }
          }
        } else if (!this.isPlaying) {
          // 如果当前没有播放，则开始播放
          audioManager.togglePlay();
        }
        return;
      }

      this.isLoading = true;

      try {
        const episodeList = await getEpisodeList(episodeId, 720);
        const audioUrl = episodeList.data.playConfig.url;

        // 更新音频管理器的状态
        audioManager.setCurrentEpisodeIndex(index);
        audioManager.setAudioSource(audioUrl, true); // 设置自动播放

        // 更新本地音频数据
        this.audioData.audioUrl = audioUrl;
      } catch (error) {
        console.error('加载音频失败:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchAudioData(audioId) {
      try {
        console.log('获取音频数据:', audioId);

        const response = await getDetail(audioId);
        this.audioData = response.data;
        console.log('Audio data loaded:', this.audioData);
        console.log('Poster URL:', this.audioData.poster);

        // 更新音频管理器的音频数据
        audioManager.setAudioData(this.audioData);

        if (!this.audioData.episodes || this.audioData.episodes.length === 0) {
          console.error('音频没有剧集数据');
          return;
        }

        let episodeId = this.audioData.episodes[0].id;
        console.log('加载第一集, ID:', episodeId);

        let episodeList = await getEpisodeList(episodeId, 720);

        if (!episodeList || !episodeList.data || !episodeList.data.playConfig) {
          console.error('获取播放配置失败');
          return;
        }

        this.audioData.audioUrl = episodeList.data.playConfig.url;
        console.log('设置音频URL:', this.audioData.audioUrl);

        // 设置音频源和初始状态
        audioManager.setAudioSource(this.audioData.audioUrl, true); // 自动播放第一集
        audioManager.setCurrentEpisodeIndex(0);
      } catch (error) {
        console.error('获取音频数据失败:', error);
      }
    },

    handlePageUnload() {
      this.notifyFloatingPlayer();
    },

    togglePlaylist() {
      this.showPlaylist = !this.showPlaylist;
    },

    hidePlaylist() {
      this.showPlaylist = false;
    },

  }
};
</script>

<style scoped>
.audio-player-page {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  height: 85vh;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  color: #fff;
  position: relative;
  backdrop-filter: blur(50px);
}

.audio-player-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: inherit;
  background-size: inherit;
  background-position: inherit;
  filter: blur(50px);
  backdrop-filter: blur(50px);
  z-index: -1;
}

/* 顶部导航栏 */
.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 10;
  height: 60px;
}

.back-button {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s;
}

.back-button:hover {
  background-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.icon-back {
  display: inline-block;
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.more-button {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s;
}

.more-button:hover {
  background-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.icon-playlist {
  display: inline-block;
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.audio-title {
  font-size: 20px;
  font-weight: 600;
  flex: 1;
  margin: 0 20px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 主要内容区域 */
.upper-container {
  height: calc(80% - 60px);
  display: flex;
}

/* 唱片区域 */
.record-container {
  height: 100%;
  margin: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
}

.record-bg {
  background-color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 400px;
  height: 400px;
  border-radius: 500px;
}

.record-img {
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 250px;
  height: 250px;
  border-radius: 250px;
  position: relative;
}

.record-center {
  background-color: black;
  width: 20px;
  height: 20px;
  border-radius: 20px;
}


/* 播放器控制区域 */
.audio-box {
  width: 100%;
  height: 20%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.audio-container {
  width: 90%;
}

/* 进度条样式 */
.a-progress {
  width: 100%;
  color: #667eea;
  background-color: transparent;
  border-radius: 10px;
  margin-bottom: 10px;
}

.pgs-total {
  width: 100%;
  height: 16px;
  background-color: transparent;
  border-radius: 10px;
  position: relative;
  cursor: pointer;
}

.a-progress .pgs-total:after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 7px;
  bottom: 7px;
  background-color: #bbbbbb;
  border-radius: 10px;
  z-index: -1;
}

.a-progress .pgs-total .pgs-play:before {
  content: '';
  position: absolute;
  top: 7px;
  bottom: 7px;
  left: 0;
  right: 0;
  background-color: #667eea;
  border-radius: 10px;
  z-index: 1;
}

.a-progress .pgs-total .pgs-play {
  height: 100%;
  position: relative;
  border-radius: 10px;
}

/* 控制按钮样式 */
.a-controls {
  width: 100%;
}

/* 时间样式 */
.time-container {
  width: 30%;
  float: left;
  color: white;
  font-weight: 300;
  line-height: 50px;
}

.played-time {
  left: 15px;
  text-align: left;
}

.audio-time {
  right: 15px;
  text-align: right;
}

/* 中间部分按钮 */
.center-button-container {
  width: 40%;
  float: left;
  display: flex;
  align-items: center;
  justify-content: center;
}

.center-icon {
  float: left;
  width: 30px;
  height: 30px;
  margin-left: 10px;
  margin-right: 10px;
  cursor: pointer;
}

.center-icon:hover {
  opacity: 0.7;
}

.center-icon:active {
  opacity: 0.6;
}

.mode {
  background-size: 100% 100%;
}

.mode-1 {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z'/%3E%3C/svg%3E");
}

.mode-2 {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z'/%3E%3C/svg%3E");
}

.mode-3 {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z'/%3E%3C/svg%3E");
}

.s-left {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 18l8.5-6L6 6v12zm10-12v12h2V6h-2z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
}

.play-pause-btn {
  float: left;
  width: 50px;
  height: 50px;
  background-size: 100% 100%;
  cursor: pointer;
}

.icon-play {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M8 5v14l11-7z'/%3E%3C/svg%3E");
}

.icon-pause {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 19h4V5H6v14zm8-14v14h4V5h-4z'/%3E%3C/svg%3E");
}

.s-right {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
}

.volume {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
}

.volume.muted {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z'/%3E%3C/svg%3E");
}

/* 音量滑块样式 */
.volume-slider {
  width: 100px;
  height: 2px;
  appearance: none;
  background-color: #dcdcdc;
  outline: none;
  overflow: hidden;
  border-radius: 15px;
  box-shadow: inset 0 0 5px rgb(91, 91, 91);
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 1px;
  height: 5px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  border: 4px solid #33333300;
  box-shadow: -407px 0 0 400px #667eea;
}

/* 右侧按钮 */
.bottom-button-container {
  width: 30%;
  padding-top: 10px;
  float: left;
  text-align: right;
}

.bottom-icon {
  float: right;
  width: 20px;
  height: 20px;
  margin-left: 10px;
  margin-right: 10px;
  cursor: pointer;
}

.bottom-icon:hover {
  opacity: 0.7;
}

.bottom-icon:active {
  opacity: 0.6;
}

.MV {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
}

.speed {
  margin-left: 10px;
  margin-right: 10px;
  float: right;
  font-weight: 300;
  color: white;
  cursor: pointer;
}

.speed:hover {
  opacity: 0.7;
}

.speed:active {
  opacity: 0.6;
}

.list {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
}

/* 播放列表 */
.close-list {
  position: fixed;
  height: 100%;
  left: 0;
  top: 0;
  width: 75%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.music-list {
  position: fixed;
  height: 100%;
  right: 0;
  top: 0;
  width: 25%;
  background-color: rgb(9 10 9 / 87%);
  direction: rtl;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
}

.music-list-container {
  width: 100%;
}

.music-list-title {
  color: white;
  font-size: 2rem;
  font-weight: 300;
  padding: 10% 15% 0% 15%;
}

.line {
  height: 1px;
  border: none;
  border-top: 1px dashed #e6e9e8;
}

.all-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
}

.all-list::-webkit-scrollbar {
  width: 6px;
}

.all-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.all-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.all-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.all-list div {
  height: 50px;
  line-height: 50px;
  color: white;
  font-weight: 300;
  padding: 0% 15% 0% 15%;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.all-list div:hover {
  background-color: rgb(27, 37, 30);
}

.all-list div.active {
  background-color: rgb(27, 37, 30);
  color: #667eea;
}

.list-card-show {
  transform: translateX(0%);
}

.list-card-hide {
  transform: translateX(100%);
}

/* 唱片旋转动画 */
.rotate-play {
  animation: rotateAni 10s infinite linear;
  animation-play-state: paused;
  animation-fill-mode: forwards;
  -webkit-animation-fill-mode: forwards;
}

.rotate-play.playing {
  animation-play-state: running;
}



@keyframes rotateAni {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .upper-container {
    width: 95%;
    padding-left: 2.5%;
    flex-direction: column;
  }

  .record-container {
    width: 100%;
    height: 60%;
  }

  .record-bg {
    width: 300px;
    height: 300px;
  }

  .record-img {
    width: 150px;
    height: 150px;
  }

  .introduction-container {
    width: 100%;
    height: 40%;
  }

  .text-container {
    width: 100%;
    padding-left: 0;
    text-align: center;
  }

  .music-title {
    font-size: 2rem;
    line-height: 3rem;
  }

  .music-list {
    width: 40%;
  }

  .close-list {
    width: 60%;
  }
}
</style>